import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Header } from '../shared/header/header';
import { Settlement } from '../payment/payment.type';
import { SettlementPaymentDialog } from '../payment/settlement-payment-dialog/settlement-payment-dialog';
import { AccountingService } from './accounting.service';
import { AuthService } from '../auth/auth.service';
import {
  ComparisonIssue, ComparisonRow, HesabfaComparison, HesabfaExport, ISSUE_LABELS, MATCH_LABELS,
} from './accounting.type';

type Tab = 'export' | 'compare';

/** Today in the business's timezone, as YYYY-MM-DD. */
function tehranToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Tehran' }).format(new Date());
}

/**
 * Hesabfa, the accountant's books. Export: send the settlements paid on chosen
 * days as purchase invoices with their payments — safe to repeat, nothing is
 * booked twice. Compare: the bot beside Hesabfa, read live, with whatever
 * differs; an edit on either side shows the next time it is compared.
 */
@Component({
  selector: 'app-accounting',
  imports: [
    CommonModule, FormsModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule,
    MatProgressSpinnerModule, MatSlideToggleModule, MatTabsModule, MatTooltipModule, Header,
  ],
  templateUrl: './accounting.html',
  styleUrl: './accounting.scss',
})
export class Accounting implements OnInit {
  private accounting = inject(AccountingService);
  readonly canEdit = inject(AuthService).canWrite('suppliers_payments');
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);

  readonly issueLabels = ISSUE_LABELS;
  readonly matchLabels = MATCH_LABELS;

  tab: Tab = 'export';

  // ── Export ──
  exportFrom = tehranToday();
  exportTo = tehranToday();
  exports: HesabfaExport[] = [];
  exportsLoading = false;
  starting = false;
  expanded = new Set<string>();

  // ── Compare ──
  compareFrom = tehranToday();
  compareTo = tehranToday();
  comparison: HesabfaComparison | null = null;
  comparing = false;
  compareError = '';
  onlyProblems = true;
  issueFilter: ComparisonIssue | '' = '';

  ngOnInit(): void {
    this.loadExports();
    // While a run is queued or running, follow it.
    interval(4000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.exports.some(e => e.status === 'Queued' || e.status === 'Running')) this.loadExports(true);
    });
  }

  get running(): boolean {
    return this.exports.some(e => e.status === 'Queued' || e.status === 'Running');
  }

  loadExports(quiet = false): void {
    if (!quiet) this.exportsLoading = true;
    this.accounting.listExports().subscribe({
      next: e => { this.exports = e; this.exportsLoading = false; },
      error: err => { this.exportsLoading = false; this.fail(err); },
    });
  }

  startExport(): void {
    if (this.starting || !this.exportFrom || !this.exportTo) return;
    const days = this.exportFrom === this.exportTo ? this.exportFrom : `${this.exportFrom} → ${this.exportTo}`;
    if (!confirm(`Send every settlement paid on ${days} to Hesabfa? What is already there is left alone.`)) return;
    this.starting = true;
    this.accounting.startExport(this.exportFrom, this.exportTo).subscribe({
      next: e => {
        this.starting = false;
        this.exports = [e, ...this.exports];
        this.snackBar.open('Export started', undefined, { duration: 2500 });
      },
      error: err => { this.starting = false; this.fail(err); },
    });
  }

  toggle(e: HesabfaExport): void {
    if (this.expanded.has(e.id)) this.expanded.delete(e.id);
    else this.expanded.add(e.id);
  }

  /** Opens the comparison on an export's days. */
  compareRun(e: HesabfaExport): void {
    this.compareFrom = e.from;
    this.compareTo = e.to;
    this.tab = 'compare';
    this.compare();
  }

  compare(): void {
    if (this.comparing || !this.compareFrom || !this.compareTo) return;
    this.comparing = true;
    this.compareError = '';
    this.accounting.compare(this.compareFrom, this.compareTo).subscribe({
      next: c => { this.comparison = c; this.comparing = false; },
      error: err => {
        this.comparing = false;
        this.compareError = err?.error?.error ?? 'Comparison failed';
      },
    });
  }

  get issueCounts(): { issue: ComparisonIssue; count: number }[] {
    const counts = this.comparison?.counts ?? {};
    return (Object.keys(ISSUE_LABELS) as ComparisonIssue[])
      .filter(i => counts[i])
      .map(issue => ({ issue, count: counts[issue] }));
  }

  get rows(): ComparisonRow[] {
    return (this.comparison?.rows ?? []).filter(r =>
      (!this.onlyProblems || r.issues.length > 0) && (!this.issueFilter || r.issues.includes(this.issueFilter)));
  }

  filterBy(issue: ComparisonIssue): void {
    this.issueFilter = this.issueFilter === issue ? '' : issue;
    if (this.issueFilter) this.onlyProblems = true;
  }

  /** Record which account a settlement was paid from, then compare again. */
  setSource(row: ComparisonRow): void {
    const s = row.bot?.settlements[0];
    if (!s) return;
    const settlement = {
      id: s.id, supplier_name: s.supplier_name, amount: Math.round(s.amount_rial / 10), paid_at: s.paid_at,
      source_account_id: s.source_account_id, bank_fee: 0,
    } as Settlement;
    this.dialog.open(SettlementPaymentDialog, { data: { settlement }, width: '460px', maxWidth: '95vw' })
      .afterClosed().subscribe(changed => { if (changed) this.compare(); });
  }

  /** No real source in the bot (none, or the unspecified account): offer to record it. */
  missingSource(row: ComparisonRow): boolean {
    return (row.issues.includes('no_source') || row.issues.includes('source_unresolved'))
      && (row.bot?.settlements.length ?? 0) === 1;
  }

  /** Coins differ only matter for coin purchases; corrections have none. */
  differs(row: ComparisonRow, issue: ComparisonIssue): boolean {
    return row.issues.includes(issue);
  }

  private fail(err: any): void {
    this.snackBar.open(err?.error?.error ?? 'Failed', 'Close', { duration: 8000 });
  }
}
